import { useState, useEffect, useRef } from "react";
import { searchLoads, type DatabaseLoad, type LoadSearchParams } from "@/lib/loadSearch";
import type { PostedTruckData } from "@/components/post-truck/PostTruckFormCard";

export interface TruckPostMatchResult {
  status: "idle" | "loading" | "ready" | "error";
  matches: DatabaseLoad[];
  matchCount: number;
  bestRate: number | null;
}

export function useTruckPostMatches(postedTruck: PostedTruckData | null): TruckPostMatchResult {
  const [result, setResult] = useState<TruckPostMatchResult>({
    status: "idle",
    matches: [],
    matchCount: 0,
    bestRate: null,
  });

  // Track which posted truck triggered the current query to avoid re-runs
  const lastRef = useRef<PostedTruckData | null>(null);

  useEffect(() => {
    if (!postedTruck) {
      setResult({ status: "idle", matches: [], matchCount: 0, bestRate: null });
      lastRef.current = null;
      return;
    }

    // Don't re-run for the same object reference
    if (lastRef.current === postedTruck) return;
    lastRef.current = postedTruck;

    let cancelled = false;

    async function run() {
      setResult((prev) => ({ ...prev, status: "loading" }));

      try {
        const params: LoadSearchParams = {
          origin: postedTruck!.origin,
          destination: postedTruck!.openToAnywhere ? "" : postedTruck!.destination,
          equipmentTypes: postedTruck!.equipmentType ? [postedTruck!.equipmentType] : [],
          loadType: postedTruck!.loadType === "FTL" ? "full" : postedTruck!.loadType === "LTL" ? "partial" : "full",
          hideCanada: false,
          searchBack: 0,
          dhoRadius: 100,
          dhdRadius: postedTruck!.openToAnywhere ? undefined : 100,
        };

        const loads = await searchLoads(params);
        if (cancelled) return;

        // Simple sort: equipment match first, then by rate descending, then by pickup freshness
        const eqType = (postedTruck!.equipmentType || "").toLowerCase();
        const sorted = [...loads].sort((a, b) => {
          const aEq = (a.equipment_type || "").toLowerCase() === eqType ? 1 : 0;
          const bEq = (b.equipment_type || "").toLowerCase() === eqType ? 1 : 0;
          if (bEq !== aEq) return bEq - aEq;

          // Higher rate first
          const aRate = a.rate_amount || 0;
          const bRate = b.rate_amount || 0;
          if (bRate !== aRate) return bRate - aRate;

          // Sooner pickup first
          return (a.pickup_date || "").localeCompare(b.pickup_date || "");
        });

        const top5 = sorted.slice(0, 5);
        const bestRate = sorted.reduce<number | null>((max, l) => {
          if (!l.rate_amount) return max;
          return max === null ? l.rate_amount : Math.max(max, l.rate_amount);
        }, null);

        setResult({
          status: "ready",
          matches: top5,
          matchCount: loads.length,
          bestRate,
        });
      } catch (err) {
        console.error("[useTruckPostMatches] Error:", err);
        if (!cancelled) {
          setResult({ status: "error", matches: [], matchCount: 0, bestRate: null });
        }
      }
    }

    run();
    return () => { cancelled = true; };
  }, [postedTruck]);

  return result;
}
